<script setup lang="ts">
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { Sun, Moon, Monitor, Globe } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import SetupProgress from '@/components/SetupProgress.vue';
import { useAppearance } from '@/composables/useAppearance';
import { setLocale, type Locale } from '@/i18n';

const { t } = useI18n();

const page = usePage<{
    step?: number;
    totalSteps?: number;
}>();

const step = computed(() => page.props.step ?? 1);
const totalSteps = computed(() => page.props.totalSteps ?? 5);

// Language
const locales = [
    { value: 'en' as Locale, label: 'English', flag: '🇬🇧' },
    { value: 'nl' as Locale, label: 'Nederlands', flag: '🇳🇱' },
    { value: 'de' as Locale, label: 'Deutsch', flag: '🇩🇪' },
];

const { locale: i18nLocale } = useI18n();
const currentLocaleData = computed(() => locales.find(l => l.value === i18nLocale.value) ?? locales[0]);
const showLangMenu = ref(false);

function changeLocale(locale: Locale) {
    setLocale(locale);
    showLangMenu.value = false;
}

// Appearance
const { appearance, updateAppearance } = useAppearance();

const themes = [
    { value: 'light' as const, icon: Sun, label: computed(() => t('setup.themeLight')) },
    { value: 'dark' as const, icon: Moon, label: computed(() => t('setup.themeDark')) },
    { value: 'system' as const, icon: Monitor, label: computed(() => t('setup.themeSystem')) },
];
</script>

<template>
    <div class="flex min-h-svh bg-background font-geist text-foreground">
        <!-- Left rail — fixed width, never grows or shrinks -->
        <aside class="sticky top-0 flex h-svh w-[280px] min-w-[280px] max-w-[280px] shrink-0 flex-col overflow-y-auto border-r border-border bg-card px-[22px] py-5">
            <!-- PillarWordmark -->
            <div class="flex items-center gap-2">
                <AppLogoIcon class="h-[22px] w-[18px] shrink-0" />
                <span class="text-[17px] font-semibold tracking-tight text-foreground" style="letter-spacing: -0.6px">Novantt</span>
            </div>

            <!-- Section label -->
            <div class="mt-7 text-[11px] font-medium uppercase text-muted-foreground" style="letter-spacing: 0.4px">Set up Novantt</div>

            <!-- Step indicators -->
            <div class="mt-4">
                <SetupProgress :step="step" :total-steps="totalSteps" />
            </div>

            <!-- Bottom section -->
            <div class="mt-auto flex flex-col gap-3 pt-6">
                <!-- Theme toggle -->
                <div>
                    <div class="mb-1.5 text-[11px] font-medium text-muted-foreground">{{ t('setup.theme') }}</div>
                    <div class="flex rounded-md border border-border bg-muted p-0.5">
                        <button
                            v-for="th in themes"
                            :key="th.value"
                            class="flex flex-1 items-center justify-center gap-1 rounded py-1 text-[11px] transition-colors"
                            :class="appearance === th.value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'"
                            @click="updateAppearance(th.value)"
                        >
                            <component :is="th.icon" class="size-3" :stroke-width="1.6" />
                            {{ th.label.value }}
                        </button>
                    </div>
                </div>

                <!-- Language selector -->
                <div class="relative">
                    <div class="mb-1.5 text-[11px] font-medium text-muted-foreground">{{ t('setup.language') }}</div>
                    <button
                        class="flex h-8 w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 text-[12px] text-foreground transition-colors hover:bg-muted"
                        @click="showLangMenu = !showLangMenu"
                    >
                        <span class="text-sm leading-none">{{ currentLocaleData.flag }}</span>
                        <span class="flex-1 text-left">{{ currentLocaleData.label }}</span>
                        <Globe class="size-3.5 text-muted-foreground" :stroke-width="1.6" />
                    </button>
                    <!-- Dropdown -->
                    <div
                        v-if="showLangMenu"
                        class="absolute bottom-full left-0 mb-1 w-full overflow-hidden rounded-md border border-border bg-card py-1 shadow-lg"
                    >
                        <button
                            v-for="l in locales"
                            :key="l.value"
                            class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-muted"
                            :class="l.value === i18nLocale ? 'font-medium text-foreground' : 'text-muted-foreground'"
                            @click="changeLocale(l.value)"
                        >
                            <span class="text-sm leading-none">{{ l.flag }}</span>
                            {{ l.label }}
                        </button>
                    </div>
                </div>

                <!-- Help link -->
                <div class="text-[11px] text-muted-foreground">
                    {{ t('setup.needHelp') }} <a href="#" class="text-nv-indigo no-underline">{{ t('setup.talkToSupport') }}</a>
                </div>
            </div>
        </aside>

        <!-- Main content -->
        <main class="min-w-0 flex-1 overflow-y-auto px-14 py-10">
            <div class="max-w-[960px]">
                <!-- Step counter -->
                <div class="text-[11px] font-medium uppercase text-muted-foreground" style="letter-spacing: 0.6px">
                    {{ t('setup.stepOf', { step }) }}
                </div>

                <!-- Page content -->
                <slot />
            </div>
        </main>
    </div>
</template>
